import { FC, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import { EGiftCardStatus, TGiftCard } from '@xeppt/xeppt-sdk/types';
import { formatDate, getGiftCardStatusVariant, prettifyAmount } from '@utils/index';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';
import { getGiftCardStatusesList } from '@utils/billing';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import Typography from '@components/common/typography';

const ViewGiftCardModal: FC<
    IModalComponentProps<{ giftCard: TGiftCard; refetchGiftCards?: () => void }>
> = ({ onClose, modalData }) => {
    const { t, i18n } = useTranslation('transaction_details');
    const { submitLocale } = useLocales();
    const { handleOpenSubmitRequestModal } = useHelpCenterModals();
    const [isListOpen, setIsListOpen] = useState(false);
    const {
        handleOpenActivateGiftCardModal,
        handleOpenAddGiftCardDescription,
        handleOpenGiftCardAddRecipientEmail
    } = useGiftCardsModals(modalData);

    const { giftCard } = modalData;

    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <div className={styles.user}>
                    <div className={styles.user_avatar}>
                        <Icon name="gift_card" />
                    </div>
                    <p className={styles.user_name}>{giftCard.gift_card_serial_number}</p>
                    <Status variant={getGiftCardStatusVariant(giftCard.status)}>
                        {
                            getGiftCardStatusesList.find((item) => item.value === giftCard.status)
                                ?.label
                        }
                    </Status>
                    <div className={styles.payment_id}>
                        <p>Gift card balance</p>
                        <p className={styles.amount}>
                            ${prettifyAmount(giftCard?.available_amount.toFixed(2) || 0)}{' '}
                            {giftCard.currency}
                        </p>
                    </div>
                </div>
                <div className={styles.details}>
                    <div className={styles.row}>
                        <p>Original amount</p>
                        <p>
                            ${prettifyAmount(giftCard?.amount.toFixed(2) || 0)} {giftCard.currency}
                        </p>
                    </div>
                    <div className={styles.row}>
                        <p>Remaining balance</p>
                        <p>
                            ${prettifyAmount(giftCard?.available_amount.toFixed(2) || 0)}{' '}
                            {giftCard.currency}
                        </p>
                    </div>
                    <div className={styles.row}>
                        <p>Issued</p>
                        <p>{formatDate(giftCard.created_at, 'DD MMMM YYYY, HH:mm')}</p>
                    </div>
                    {giftCard.points > 0 && (
                        <div className={styles.row}>
                            <p>Points you earned</p>
                            <p>
                                ${giftCard?.points} {giftCard.currency}
                            </p>
                        </div>
                    )}
                    <div className={styles.row}>
                        <p>Description</p>
                        <p>
                            {giftCard?.description ? (
                                <div>
                                    {giftCard?.description}{' '}
                                    {giftCard.status === EGiftCardStatus.ISSUED && (
                                        <Button
                                            leftIcon="edit"
                                            variant="text-gold"
                                            size="small"
                                            onClick={() => {
                                                handleOpenAddGiftCardDescription(
                                                    modalData.giftCard.id,
                                                    true,
                                                    modalData.giftCard
                                                );
                                            }}
                                        />
                                    )}
                                </div>
                            ) : giftCard.status === EGiftCardStatus.ISSUED ? (
                                <Button
                                    variant="text-gold"
                                    leftIcon="plus"
                                    size="small"
                                    onClick={() => {
                                        handleOpenAddGiftCardDescription(
                                            modalData.giftCard.id,
                                            false,
                                            modalData.giftCard
                                        );
                                    }}>
                                    Add description
                                </Button>
                            ) : (
                                '-'
                            )}
                        </p>
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.recipient}>
                        <div className={styles.header}>
                            <Icon name="email" />
                            Delivery info
                        </div>
                        <div className={styles.details}>
                            {giftCard.email && (
                                <div className={styles.row}>
                                    <p>Recipient email</p>
                                    <p className={styles.row_value}>
                                        {giftCard?.email || '-'}{' '}
                                        {giftCard.status === EGiftCardStatus.ISSUED && (
                                            <Button
                                                leftIcon="edit"
                                                variant="text-gold"
                                                size="small"
                                                onClick={() =>
                                                    handleOpenGiftCardAddRecipientEmail(
                                                        giftCard.id,
                                                        true,
                                                        giftCard
                                                    )
                                                }
                                            />
                                        )}
                                    </p>
                                </div>
                            )}
                            {giftCard.phone_code && giftCard.phone_number && (
                                <div className={styles.row}>
                                    <p>Recipient phone</p>
                                    <p className={styles.row_value}>
                                        {giftCard.phone_code.includes('+')
                                            ? giftCard.phone_code
                                            : '+' + giftCard.phone_code}{' '}
                                        {giftCard.phone_number}{' '}
                                        {giftCard.status === EGiftCardStatus.ISSUED && (
                                            <Button
                                                leftIcon="edit"
                                                variant="text-gold"
                                                size="small"
                                                onClick={() =>
                                                    handleOpenGiftCardAddRecipientEmail(
                                                        giftCard.id,
                                                        true,
                                                        giftCard
                                                    )
                                                }
                                            />
                                        )}
                                    </p>
                                </div>
                            )}
                            {(!giftCard.phone_code || !giftCard.phone_number) &&
                                !giftCard.email &&
                                (giftCard.status === EGiftCardStatus.ISSUED ? (
                                    <Button
                                        variant="text-gold"
                                        leftIcon="plus"
                                        size="small"
                                        onClick={() =>
                                            handleOpenGiftCardAddRecipientEmail(
                                                giftCard.id,
                                                false,
                                                giftCard
                                            )
                                        }>
                                        Add recipient details
                                    </Button>
                                ) : (
                                    '-'
                                ))}
                            {giftCard.notification_sent && giftCard.notification_sent_at && (
                                <div className={styles.row}>
                                    <p>Sent</p>
                                    <p>
                                        {formatDate(
                                            giftCard.notification_sent_at,
                                            'DD MMMM YYYY, HH:mm'
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.divider} />
                    {(giftCard?.redemption_history?.length || 0) !== 0 && (
                        <div className={styles.recipient}>
                            <div
                                className={`${styles.header} ${styles.active_header}`}
                                onClick={() => setIsListOpen((state) => !state)}>
                                <Icon name="redeem" />
                                Redemption history
                                <Icon name="nav_down" />
                            </div>
                            <div
                                className={`${styles.transactions_list} ${isListOpen ? styles.isOpen : ''}`}>
                                {giftCard?.redemption_history?.map((item, i) => {
                                    return (
                                        <div key={i} className={styles.item}>
                                            <div className={styles.left}>
                                                <Typography variant="body4">
                                                    {item.description}
                                                </Typography>
                                                <Typography variant="body4" className={styles.date}>
                                                    {formatDate(
                                                        item.created_at,
                                                        'DD MMM YYYY, HH:mm'
                                                    )}
                                                </Typography>
                                            </div>
                                            <Typography variant="body2" weight="semibold">
                                                -${prettifyAmount(item.amount.toFixed(2))}
                                            </Typography>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {(giftCard.status === EGiftCardStatus.ISSUED ||
                giftCard.status === EGiftCardStatus.PENDING) && (
                <div className={styles.actions_wrapper}>
                    <div className={styles.actions}>
                        {giftCard.status === EGiftCardStatus.ISSUED && (
                            <Button
                                variant="primary"
                                size="medium"
                                onClick={() => {
                                    handleOpenActivateGiftCardModal([modalData.giftCard]);
                                }}>
                                {submitLocale('activate')}
                            </Button>
                        )}
                        {(giftCard.status === EGiftCardStatus.ISSUED ||
                            giftCard.status === EGiftCardStatus.PENDING) && (
                            <Button
                                variant="outlined-red"
                                size="medium"
                                onClick={() => {
                                    handleOpenSubmitRequestModal(
                                        undefined,
                                        undefined,
                                        giftCard.gift_card_serial_number
                                    );
                                }}>
                                {submitLocale('cancel')}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewGiftCardModal;
